import { useState, useRef, useEffect } from 'react'
import { ChevronDown, ChevronUp, Check, Copy, RotateCcw, Pencil } from 'lucide-react'
import type { Creds } from '../types'

// ── Constants ─────────────────────────────────────────────────────────────────

export const inputCls = 'w-full bg-[#0a1322] border border-[#233149] rounded-[7px] px-2.5 py-[6px] text-[12px] text-[#e2e8f0] placeholder:text-[#3f4f6b] focus:outline-none focus:border-[#6366f1]/50 font-mono'
export const labelCls = 'block text-[9px] font-semibold uppercase tracking-wider text-[#5b6b85] mb-1'

// ── Helpers ───────────────────────────────────────────────────────────────────

export function syntaxHighlight(json: string): string {
  const escaped = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return escaped.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
    (match) => {
      let style = 'color:#d19a66' // number — orange
      if (/^"/.test(match)) {
        style = /:$/.test(match) ? 'color:#7eb3f5' : 'color:#98c379' // key=blue, string=green
      } else if (/true|false/.test(match)) {
        style = 'color:#56b6c2' // boolean — cyan
      } else if (/null/.test(match)) {
        style = 'color:#f87171' // null — red
      }
      return `<span style="${style}">${match}</span>`
    }
  )
}

export function buildCurl(method: string, url: string, creds: Creds, body?: unknown): string {
  const lines = [`curl -X ${method} '${url}'`]
  lines.push(`  -H 'X-FT-Customer-ID: ${creds.customerId}'`)
  lines.push(`  -H 'X-FT-API-Key: ${creds.apiKey}'`)
  if (method !== 'GET') lines.push(`  -H 'Content-Type: application/json'`)
  if (body) lines.push(`  -d '${JSON.stringify(body)}'`)
  return lines.join(' \\\n')
}

// ── Components ────────────────────────────────────────────────────────────────

export function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="text-[9px] font-semibold uppercase tracking-wider text-[#5b6b85] mb-2">{children}</p>
}

export function ResultBadge({ result }: { result: string }) {
  const r = result.toUpperCase()
  const style = r === 'MATCH' || r === 'CLEAR' || r === 'PASS'
    ? { bg: 'rgba(52,211,153,.15)', color: '#34d399' }
    : r === 'NO_MATCH' || r === 'FAIL'
    ? { bg: 'rgba(248,113,113,.15)', color: '#f87171' }
    : r === 'REFER' || r === 'REVIEW' || r === 'MEDIUM'
    ? { bg: 'rgba(251,146,60,.15)', color: '#fb923c' }
    : { bg: 'rgba(148,163,184,.15)', color: '#94a3b8' }
  return (
    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: style.bg, color: style.color }}>
      {result}
    </span>
  )
}

export function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={() => { void navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
      className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] transition-colors"
      style={{ background: '#111c30', border: '1px solid #233149', color: '#5b6b85' }}
    >
      {copied ? <Check size={9} className="text-emerald-400" /> : <Copy size={9} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}

export function RawJsonCollapsible({ data, label = 'Raw JSON Response' }: { data: unknown; label?: string }) {
  const [open, setOpen] = useState(false)
  const json = JSON.stringify(data, null, 2)
  return (
    <div className="rounded-[11px] overflow-hidden" style={{ border: '1px solid #1e2c44' }}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 transition-colors hover:bg-[#111c30]"
        style={{ background: '#0e1828' }}
      >
        <span className="text-[10px] font-semibold uppercase tracking-wider text-[#5b6b85]">{label}</span>
        {open ? <ChevronUp size={11} className="text-[#5b6b85]" /> : <ChevronDown size={11} className="text-[#5b6b85]" />}
      </button>
      {open && (
        <div className="p-3" style={{ background: '#0a1322', borderTop: '1px solid #1e2c44', maxHeight: 320, overflowY: 'auto' }}>
          <div className="flex justify-end mb-2">
            <CopyButton text={json} />
          </div>
          <pre className="text-[10px] text-[#94a3b8] font-mono whitespace-pre-wrap break-all">{json}</pre>
        </div>
      )}
    </div>
  )
}

export function CurlCollapsible({ method, url, creds, body }: { method: string; url: string; creds: Creds; body?: unknown }) {
  const [open, setOpen] = useState(false)
  const curl = buildCurl(method, url, creds, body)
  return (
    <div className="rounded-[9px] overflow-hidden" style={{ border: '1px solid #1a2740' }}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 transition-colors"
        style={{ background: '#0d1626' }}
      >
        <span className="text-[9px] font-semibold uppercase tracking-wider text-[#3f4f6b]">cURL</span>
        {open ? <ChevronUp size={10} className="text-[#3f4f6b]" /> : <ChevronDown size={10} className="text-[#3f4f6b]" />}
      </button>
      {open && (
        <div className="p-2.5" style={{ background: '#0a1322', borderTop: '1px solid #1a2740' }}>
          <div className="flex justify-end mb-1.5">
            <CopyButton text={curl} />
          </div>
          <pre className="text-[10px] text-[#8aa0c2] font-mono whitespace-pre-wrap break-all">{curl}</pre>
        </div>
      )}
    </div>
  )
}

export interface PayloadEditorProps {
  value: string
  dirty: boolean
  error: string | null
  onChange: (v: string) => void
  onReset: () => void
}

export function PayloadEditor({ value, dirty, error, onChange, onReset }: PayloadEditorProps) {
  const [editing, setEditing] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  function startEdit() {
    setEditing(true)
    requestAnimationFrame(() => textareaRef.current?.focus())
  }

  function handleBlur() {
    setEditing(false)
    // Try to pretty-print on blur if valid JSON
    try {
      onChange(JSON.stringify(JSON.parse(value), null, 2))
    } catch { /* leave as-is */ }
  }

  return (
    <div className="rounded-[14px] overflow-hidden" style={{ background: '#111c30', border: `1px solid ${error ? 'rgba(248,113,113,.4)' : '#1e2c44'}` }}>
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2.5" style={{ background: '#0e1828', borderBottom: '1px solid #1e2c44' }}>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-[#5b6b85] flex-1">Request Payload</span>
        {dirty ? (
          <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold" style={{ background: 'rgba(251,191,36,.1)', color: '#fcd34d', border: '1px solid rgba(251,191,36,.25)' }}>
            <Pencil size={8} />
            edited
          </span>
        ) : (
          <span className="text-[9px] text-[#3f4f6b]">synced from form</span>
        )}
        {dirty && (
          <button
            onClick={() => { onReset(); setEditing(false) }}
            className="flex items-center gap-1 px-2 py-0.5 rounded text-[9px] transition-colors hover:text-[#94a3b8]"
            style={{ color: '#5b6b85' }}
            title="Reset to form values"
          >
            <RotateCcw size={9} />
            Reset
          </button>
        )}
        {!editing && (
          <button
            onClick={startEdit}
            className="flex items-center gap-1 px-2 py-0.5 rounded text-[9px] transition-colors hover:text-[#94a3b8]"
            style={{ color: '#5b6b85' }}
          >
            <Pencil size={9} />
            Edit
          </button>
        )}
        <CopyButton text={value} />
      </div>

      {/* Editor body */}
      <div className="relative" style={{ background: '#0a1322' }}>
        {editing ? (
          <textarea
            ref={textareaRef}
            value={value}
            onChange={e => onChange(e.target.value)}
            onBlur={handleBlur}
            spellCheck={false}
            className="w-full font-mono text-[11px] p-3 focus:outline-none resize-none"
            style={{
              background: 'transparent',
              color: '#e2e8f0',
              minHeight: 180,
              maxHeight: 420,
              lineHeight: 1.6,
              caretColor: '#7eb3f5',
            }}
            rows={Math.min(Math.max(value.split('\n').length, 6), 22)}
          />
        ) : (
          <pre
            onClick={startEdit}
            className="p-3 text-[11px] font-mono cursor-text overflow-x-auto"
            style={{ color: '#5b6b85', lineHeight: 1.6, minHeight: 60, maxHeight: 420, overflowY: 'auto' }}
            // eslint-disable-next-line @typescript-eslint/naming-convention
            dangerouslySetInnerHTML={{ __html: syntaxHighlight(value) }}
          />
        )}
      </div>

      {error && (
        <div className="px-3 py-2 text-[10px] font-mono" style={{ background: 'rgba(248,113,113,.08)', borderTop: '1px solid rgba(248,113,113,.2)', color: '#f87171' }}>
          {error}
        </div>
      )}
    </div>
  )
}

// ── EndpointPicker ─────────────────────────────────────────────────────────────

export interface EndpointDef {
  id: string
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  label: string
  path: string
  description?: string
  category?: string // categorized group (collapsible). Endpoints without category go to "Core Flow" top section
  core?: boolean    // if true, also appears in "Core Flow" even if it has a category
}

export interface EndpointPickerProps {
  endpoints: EndpointDef[]
  selectedId: string
  onSelect: (id: string) => void
}

function methodBadgeStyle(method: EndpointDef['method']): { background: string; color: string } {
  if (method === 'GET') return { background: 'rgba(96,165,250,.15)', color: '#60a5fa' }
  if (method === 'POST') return { background: 'rgba(52,211,153,.13)', color: '#34d399' }
  if (method === 'PUT' || method === 'PATCH') return { background: 'rgba(251,146,60,.15)', color: '#fb923c' }
  return { background: 'rgba(248,113,113,.15)', color: '#f87171' }
}

function EndpointButton({ ep, isSelected, onSelect, compact }: { ep: EndpointDef; isSelected: boolean; onSelect: () => void; compact?: boolean }) {
  const badgeStyle = methodBadgeStyle(ep.method)
  return (
    <button
      onClick={onSelect}
      className="w-full text-left rounded-[6px] transition-all"
      style={{
        padding: compact ? '4px 8px 4px 10px' : '6px 10px 6px 10px',
        borderLeft: isSelected ? '2px solid #6366f1' : '2px solid transparent',
        background: isSelected ? 'rgba(99,102,241,.08)' : 'transparent',
      }}
    >
      <div className="flex items-center gap-1.5">
        <span className="text-[7px] font-bold font-mono px-1 py-[1px] rounded shrink-0 leading-tight" style={badgeStyle}>
          {ep.method === 'DELETE' ? 'DEL' : ep.method}
        </span>
        <span className="text-[10px] font-semibold truncate" style={{ color: isSelected ? '#e2e8f0' : '#8896ab' }}>{ep.label}</span>
      </div>
    </button>
  )
}

export function EndpointPicker({ endpoints, selectedId, onSelect }: EndpointPickerProps) {
  // Core Flow = endpoints without a category + any with core:true
  const coreEndpoints = endpoints.filter(ep => !ep.category || ep.core)
  // Categories include ALL endpoints that have that category (even if also core)
  const categoryOrder: string[] = []
  const categoryMap: Record<string, EndpointDef[]> = {}
  for (const ep of endpoints) {
    if (!ep.category) continue
    if (!categoryMap[ep.category]) {
      categoryOrder.push(ep.category)
      categoryMap[ep.category] = []
    }
    categoryMap[ep.category].push(ep)
  }

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {}
    for (const cat of categoryOrder) init[cat] = true
    return init
  })

  const toggleCat = (cat: string) => setCollapsed(c => ({ ...c, [cat]: !c[cat] }))

  return (
    <div
      className="rounded-[9px] overflow-y-auto"
      style={{ background: '#0c1525', border: '1px solid #1a2740', maxHeight: 'calc(100vh - 200px)' }}
    >
      {/* Core Flow */}
      {coreEndpoints.length > 0 && (
        <div className="px-1.5 pt-1.5 pb-1">
          <p className="text-[7px] font-bold uppercase tracking-[.12em] px-2 pb-1" style={{ color: '#4f5d75' }}>Core Flow</p>
          {coreEndpoints.map(ep => (
            <EndpointButton key={ep.id} ep={ep} isSelected={ep.id === selectedId} onSelect={() => onSelect(ep.id)} />
          ))}
        </div>
      )}

      {/* Categorized — collapsed */}
      {categoryOrder.length > 0 && (
        <div style={{ borderTop: '1px solid #1a2740' }}>
          {categoryOrder.map(cat => {
            const eps = categoryMap[cat]
            const isCollapsed = collapsed[cat]
            const hasSelected = eps.some(ep => ep.id === selectedId)
            return (
              <div key={cat}>
                <button
                  onClick={() => toggleCat(cat)}
                  className="w-full flex items-center justify-between px-3 py-[5px]"
                  style={cat !== categoryOrder[0] ? { borderTop: '1px solid #141f30' } : undefined}
                >
                  <span className="text-[7px] font-bold uppercase tracking-[.12em]" style={{ color: hasSelected ? '#818cf8' : '#3f4f6b' }}>
                    {cat}
                  </span>
                  <div className="flex items-center gap-1">
                    <span className="text-[7px] font-mono" style={{ color: '#2d3d55' }}>{eps.length}</span>
                    <ChevronDown
                      size={9}
                      style={{ color: '#3f4f6b', transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform .15s' }}
                    />
                  </div>
                </button>
                {!isCollapsed && (
                  <div className="px-1.5 pb-1">
                    {eps.map(ep => (
                      <EndpointButton key={ep.id} ep={ep} isSelected={ep.id === selectedId} onSelect={() => onSelect(ep.id)} compact />
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── ComboSelect — input with dropdown suggestions ────────────────────────────

export interface ComboOption {
  value: string
  label?: string
}

export function ComboSelect({
  value,
  onChange,
  options,
  placeholder,
  className,
  style,
}: {
  value: string
  onChange: (v: string) => void
  options: ComboOption[]
  placeholder?: string
  className?: string
  style?: React.CSSProperties
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const filtered = options.filter(o =>
    !value || o.value.toLowerCase().includes(value.toLowerCase()) || (o.label && o.label.toLowerCase().includes(value.toLowerCase()))
  )

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <input
          value={value}
          onChange={e => { onChange(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className={className ?? inputCls}
          style={style}
        />
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-[#1e2c44] transition-colors"
          style={{ color: '#5b6b85' }}
          tabIndex={-1}
        >
          <ChevronDown size={11} />
        </button>
      </div>
      {open && filtered.length > 0 && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 rounded-lg overflow-hidden overflow-y-auto"
          style={{ background: '#0e1828', border: '1px solid #233149', maxHeight: 180 }}
        >
          {filtered.map(o => (
            <button
              key={o.value}
              type="button"
              onClick={() => { onChange(o.value); setOpen(false) }}
              className="w-full text-left px-3 py-1.5 text-[11px] font-mono hover:bg-[#1a2740] transition-colors flex items-center gap-2"
              style={{ color: value === o.value ? '#818cf8' : '#94a3b8' }}
            >
              <span className="font-semibold">{o.value}</span>
              {o.label && <span style={{ color: '#5b6b85' }}>— {o.label}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
